import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Collapse, Typography } from '@mui/material';

import { QuestionTitleHtml } from './QuestionTitleHtml';
import { parseQuestionDate } from './questionValidation';
import { SectionHeading } from './SectionHeading';
import { classes } from './styles';
import FormCheckboxGroup from '../NewUI/Components/CustomUIElements/FormCheckboxGroup';
import FormRadioButtonGroup from '../NewUI/Components/CustomUIElements/FormRadioButtonGroup';
import FormRankField from '../NewUI/Components/CustomUIElements/FormRankField';
import FormSelectField from '../NewUI/Components/CustomUIElements/FormSelectField';
import {
  FormTextField,
  MultilineFormTextField
} from '../NewUI/Components/CustomUIElements/FormTextField';
import { SingleDatePicker } from '../NewUI/Components/CustomUIElements/SingleDatePicker';

import type { Dayjs } from 'dayjs';

import type { Answer } from './questionTypes';

const FOCUSABLE_INPUT_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])';

const GROUPED_FIELD_TYPES = new Set(['radio_button_field', 'check_box_field', 'rank_field']);
const FELIX_FEEDBACK_TEXT = "Get Felix AI's feedback on my answer";
// keep in sync with AiStudio::AiInterview::MAX_INPUT_LENGTH
export const FELIX_FEEDBACK_MAX_LENGTH = 4000;

const choiceHtmlLabel = (html: string) => <QuestionTitleHtml html={html} />;

/**
 * Given the DOM node that a wrapper's innerRef points to, return the precise
 * focusable element for that field type.  Uses matches() first (the node
 * itself may already be the target, e.g. FormTextField's inputRef), then
 * falls back to querySelector inside the node.
 */
const extractFocusTarget = (node: Element | null, fieldType: string): HTMLElement | null => {
  if (!(node instanceof HTMLElement)) return null;

  switch (fieldType) {
    case 'radio_button_field':
      return node.matches('input[type="radio"]')
        ? node
        : node.querySelector<HTMLElement>('input[type="radio"]');
    case 'check_box_field':
      return node.matches('input[type="checkbox"]')
        ? node
        : node.querySelector<HTMLElement>('input[type="checkbox"]');
    case 'select_field':
    case 'rank_field':
      return node.matches('[role="combobox"]')
        ? node
        : node.querySelector<HTMLElement>('[role="combobox"]');
    case 'date_field':
      return node.matches('input') ? node : node.querySelector<HTMLElement>('input');
    case 'text_field':
    case 'text_area_field':
    default:
      return node.matches(FOCUSABLE_INPUT_SELECTOR)
        ? node
        : node.querySelector<HTMLElement>(FOCUSABLE_INPUT_SELECTOR);
  }
};

export interface QuestionsProps {
  answers: Answer[];
  aiInterviewEnabled?: boolean;
  fontFamily?: string;
  /** Called whenever answers state changes. Used by ApplicationFlow to run pure validation.
   *  changedFieldId identifies the specific field that was edited (null on bulk/init). */
  onAnswersChange?: (answers: Answer[], changedFieldId: number | null) => void;
  /** Error messages keyed by field.id, set by parent after validation. */
  errorsByFieldId?: Record<number, string>;
  /** Focus signal from parent. New object reference each time to guarantee
   *  the effect re-fires even when the same field fails validation twice. */
  focusSignal?: { fieldId: number; tick: number } | null;
}

export const Questions: React.FC<QuestionsProps> = ({
  answers: answersData,
  aiInterviewEnabled = false,
  fontFamily,
  onAnswersChange,
  errorsByFieldId = {},
  focusSignal = null
}) => {
  const [answers, setAnswers] = useState<Answer[]>(answersData);
  const answersRef = useRef<Answer[]>(answers);
  const questionRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const controlRefs = useRef<Map<number, HTMLElement | null>>(new Map());
  const errorsByFieldIdMap = useMemo(
    () =>
      new Map<number, string>(
        Object.entries(errorsByFieldId).map(([fieldId, error]) => [Number(fieldId), error])
      ),
    [errorsByFieldId]
  );

  // Keep updater side-effect free by computing next outside React state updater.
  const updateAnswers = useCallback(
    (updater: (prev: Answer[]) => Answer[], changedFieldId: number | null) => {
      const next = updater(answersRef.current);
      answersRef.current = next;
      setAnswers(next);
      onAnswersChange?.(next, changedFieldId);
    },
    [onAnswersChange]
  );

  // Focus/scroll to the first invalid question requested by the parent. Scroll targets the
  // question container; focus targets the precise control resolved at mount via innerRef +
  // extractFocusTarget. focusSignal is a fresh object per failure (tick increments), so the
  // effect re-fires even when the same field fails validation repeatedly.
  useEffect(() => {
    if (focusSignal == null) return;
    const { fieldId } = focusSignal;

    const container = questionRefs.current.get(fieldId);
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const control = controlRefs.current.get(fieldId);
    if (control) {
      control.focus({ preventScroll: true });
    } else if (container) {
      container.focus({ preventScroll: true });
    }
  }, [focusSignal]);

  const toChoiceId = (value: string | number): number | undefined => {
    if (value === '') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const fieldIdAt = (index: number): number | null =>
    answersRef.current.at(index)?.field?.id ?? null;

  const handleRadioChange = (index: number, value: string | number) => {
    const choiceId = toChoiceId(value);
    updateAnswers(
      (prev) =>
        prev.map((answer, i) => (i === index ? { ...answer, field_choice_id: choiceId } : answer)),
      fieldIdAt(index)
    );
  };

  const handleCheckboxChange = (index: number, checkedValues: Record<string, boolean>) => {
    const selectedIds = Object.entries(checkedValues)
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => Number(id));

    updateAnswers(
      (prev) =>
        prev.map((answer, i) =>
          i === index ? { ...answer, field_choice_ids: selectedIds } : answer
        ),
      fieldIdAt(index)
    );
  };

  const handleSelectChange = (index: number, value: string | number) => {
    const choiceId = toChoiceId(value);
    updateAnswers(
      (prev) =>
        prev.map((answer, i) => (i === index ? { ...answer, field_choice_id: choiceId } : answer)),
      fieldIdAt(index)
    );
  };

  const handleTextChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    updateAnswers(
      (prev) => prev.map((answer, i) => (i === index ? { ...answer, data: value } : answer)),
      fieldIdAt(index)
    );
  };

  const handleDateChange = (index: number, date: Dayjs | null) => {
    const dateValue = date ? date.format('DD/MM/YYYY') : '';
    updateAnswers(
      (prev) => prev.map((answer, i) => (i === index ? { ...answer, data: dateValue } : answer)),
      fieldIdAt(index)
    );
  };

  const handleRankChange = (index: number, ranks: Record<string | number, string>) => {
    updateAnswers(
      (prev) => prev.map((answer, i) => (i === index ? { ...answer, data: ranks } : answer)),
      fieldIdAt(index)
    );
  };

  const answerDataInputId = (index: number) => `application_answers_attributes_${index}_data`;

  const canShowFelixFeedback = (answer: Answer) =>
    aiInterviewEnabled &&
    answer.field.field_type === 'text_area_field' &&
    answer.field.felix_ai_interview_prompter;

  const renderFelixFeedback = (answer: Answer, index: number) => {
    if (!canShowFelixFeedback(answer)) return null;

    return (
      <>
        <Box
          aria-hidden
          id={`application_answers_attributes_${index}_felix_reference`}
          sx={classes.questions.felixReferenceSx()}
        />
        <Box
          className="felix-container wcag-felix-container"
          sx={classes.questions.felixContainerSx()}
        >
          <Box
            alt="Ask Felix Icon"
            className="ask-felix-icon"
            component="img"
            src="/static/felix-icon.svg"
            sx={classes.questions.felixIconSx()}
          />
          <Box
            className="felix"
            component="button"
            id={`trigger-react-component-${answer.field.id}`}
            sx={classes.questions.felixButtonSx(fontFamily)}
            type="button"
          >
            {FELIX_FEEDBACK_TEXT}
          </Box>
        </Box>
        <Box
          className="wcag-ai-interview-root"
          id="react-component-root"
          sx={classes.questions.aiInterviewRootSx()}
        />
      </>
    );
  };

  const renderField = (answer: Answer, index: number, hasError = false) => {
    const { field } = answer;

    if (!field) {
      return null;
    }

    switch (field.field_type) {
      case 'radio_button_field':
        return (
          <>
            <Box sx={classes.questions.radioCheckboxGroupSx(fontFamily)}>
              <FormRadioButtonGroup
                defaultValue={answer.field_choice_id}
                onChange={(value) => handleRadioChange(index, value)}
                required={field.required}
                innerRef={(node: Element | null) => {
                  if (node)
                    controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                  else controlRefs.current.delete(field.id);
                }}
                options={
                  field.field_choices?.map((choice) => ({
                    label: choiceHtmlLabel(choice.name),
                    value: choice.id
                  })) ?? []
                }
              />
            </Box>
            <input
              data-field-index={index}
              data-required={field.required}
              name={`application[answers_attributes][${index}][field_choice_id]`}
              type="hidden"
              value={answer.field_choice_id ?? ''}
            />
          </>
        );

      case 'check_box_field': {
        const checkboxDefaultValues =
          field.field_choices?.reduce((acc, choice) => {
            acc[choice.id] = (answer.field_choice_ids ?? []).includes(choice.id);
            return acc;
          }, {}) ?? {};

        return (
          <>
            <Box sx={classes.questions.radioCheckboxGroupSx(fontFamily)}>
              <FormCheckboxGroup
                defaultValues={checkboxDefaultValues}
                onChange={(value) => handleCheckboxChange(index, value)}
                required={field.required}
                innerRef={(node: Element | null) => {
                  if (node)
                    controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                  else controlRefs.current.delete(field.id);
                }}
                options={
                  field.field_choices?.map((choice) => ({
                    name: choice.name,
                    id: choice.id,
                    tip: '',
                    label: choiceHtmlLabel(choice.name)
                  })) ?? []
                }
              />
            </Box>
            {(answer.field_choice_ids ?? []).map((choiceId) => (
              <input
                key={choiceId}
                name={`application[answers_attributes][${index}][field_choice_ids][]`}
                type="hidden"
                value={choiceId}
              />
            ))}
          </>
        );
      }

      case 'select_field':
        return (
          <>
            <FormSelectField
              defaultValue={answer.field_choice_id != null ? String(answer.field_choice_id) : ''}
              onChange={(value) => handleSelectChange(index, value)}
              required={field.required}
              styles={classes.shared.selectFieldStyles(hasError)}
              innerRef={(node: Element | null) => {
                if (node)
                  controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                else controlRefs.current.delete(field.id);
              }}
              options={
                field.field_choices?.map((choice) => ({
                  label: choiceHtmlLabel(choice.name),
                  value: String(choice.id)
                })) ?? []
              }
            />
            <input
              name={`application[answers_attributes][${index}][field_choice_id]`}
              type="hidden"
              value={answer.field_choice_id ?? ''}
            />
          </>
        );

      case 'text_area_field':
        return (
          <>
            <Box className="field-container" sx={classes.questions.felixFieldContainerSx()}>
              <MultilineFormTextField
                fullWidth
                id={answerDataInputId(index)}
                maxLength={canShowFelixFeedback(answer) ? FELIX_FEEDBACK_MAX_LENGTH : undefined}
                onChange={(e) => handleTextChange(index, e)}
                required={field.required}
                rows={3}
                styles={classes.questions.inputStyles(fontFamily)}
                value={String(answer.data ?? '')}
                innerRef={(node: Element | null) => {
                  if (node)
                    controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                  else controlRefs.current.delete(field.id);
                }}
              />
              {renderFelixFeedback(answer, index)}
            </Box>
            <input
              name={`application[answers_attributes][${index}][data]`}
              type="hidden"
              value={String(answer.data ?? '')}
            />
          </>
        );

      case 'date_field': {
        const dateValue = answer.data
          ? parseQuestionDate(String(answer.data), { format: 'DD/MM/YYYY', strict: false }).toDate()
          : null;
        const setDateFieldRef = (node: Element | null) => {
          if (node) controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
          else controlRefs.current.delete(field.id);
        };

        return (
          <>
            <Box sx={classes.questions.datePickerWrapperSx()}>
              <SingleDatePicker
                innerRef={setDateFieldRef}
                onChange={(date) => handleDateChange(index, date)}
                required={field.required}
                slots={{ openPickerIcon: CalendarMonthIcon }}
                value={dateValue}
                inputStyles={classes.questions.datePickerSx(
                  fontFamily,
                  hasError,
                  Boolean(answer.data)
                )}
              />
            </Box>
            <input
              name={`application[answers_attributes][${index}][data]`}
              type="hidden"
              value={String(answer.data ?? '')}
            />
          </>
        );
      }

      case 'text_field':
        return (
          <>
            <FormTextField
              fullWidth
              onChange={(e) => handleTextChange(index, e)}
              required={field.required}
              styles={classes.questions.inputStyles(fontFamily)}
              value={String(answer.data ?? '')}
              innerRef={(node: Element | null) => {
                if (node)
                  controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                else controlRefs.current.delete(field.id);
              }}
            />
            <input
              name={`application[answers_attributes][${index}][data]`}
              type="hidden"
              value={String(answer.data ?? '')}
            />
          </>
        );

      case 'rank_field': {
        // TODO: align rank-field UI with Smart Forms "new question" rank style (spacing, input shape, label alignment)
        const rankData = typeof answer.data === 'object' && answer.data !== null ? answer.data : {};
        const rankDefaultValue = Object.entries(rankData).reduce<Record<string, number>>(
          (acc, [key, value]) => ({
            ...acc,
            [key]: Number(value)
          }),
          {}
        );

        return (
          <Box sx={classes.questions.rankFieldContainerSx(fontFamily, hasError)}>
            <FormRankField
              defaultValue={rankDefaultValue}
              onChange={(ranks) => handleRankChange(index, ranks)}
              renderChoiceName={(choice) => choiceHtmlLabel(choice.name)}
              required={field.required}
              choices={
                field.field_choices?.map((choice) => ({
                  id: choice.id,
                  name: choice.name,
                  position: choice.position ?? 0
                })) ?? []
              }
              innerRef={(node: Element | null) => {
                if (node)
                  controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                else controlRefs.current.delete(field.id);
              }}
            />
            {typeof answer.data === 'object' &&
              answer.data !== null &&
              Object.entries(answer.data).map(([choiceId, rank]) => (
                <input
                  key={choiceId}
                  name={`application[answers_attributes][${index}][data][${choiceId}]`}
                  type="hidden"
                  value={String(rank)}
                />
              ))}
          </Box>
        );
      }

      default:
        return (
          <>
            <FormTextField
              fullWidth
              onChange={(e) => handleTextChange(index, e)}
              required={field.required}
              styles={classes.questions.inputStyles(fontFamily)}
              value={String(answer.data ?? '')}
              innerRef={(node: Element | null) => {
                if (node)
                  controlRefs.current.set(field.id, extractFocusTarget(node, field.field_type));
                else controlRefs.current.delete(field.id);
              }}
            />
            <input
              name={`application[answers_attributes][${index}][data]`}
              type="hidden"
              value={String(answer.data ?? '')}
            />
          </>
        );
    }
  };

  if (!answers || answers.length === 0) {
    return null;
  }

  return (
    <Box
      aria-labelledby="questions-heading"
      component="section"
      sx={classes.questions.containerSx()}
    >
      <Box className="questions-container">
        <SectionHeading fontFamily={fontFamily} id="questions-heading">
          Questions
        </SectionHeading>

        <Box className="questions-list">
          {answers.map((answer, index) => {
            if (!answer.field) {
              return null;
            }

            const fieldError = errorsByFieldIdMap.get(answer.field.id);

            return (
              <Box
                key={answer.field.id}
                ref={(el: HTMLDivElement | null) => {
                  questionRefs.current.set(answer.field.id, el);
                }}
                data-question-container
                id={`question-${answer.field.id}`}
                sx={classes.questions.questionItemSx(fontFamily, !!fieldError)}
                tabIndex={-1}
              >
                <Typography
                  data-question-label
                  component="label"
                  htmlFor={
                    answer.field.field_type === 'text_area_field'
                      ? answerDataInputId(index)
                      : undefined
                  }
                  sx={classes.questions.questionLabelSx(
                    fontFamily,
                    GROUPED_FIELD_TYPES.has(answer.field.field_type) ? 'grouped' : 'regular'
                  )}
                >
                  {`${index + 1}. `}
                  <QuestionTitleHtml html={answer.field.title} />
                  {answer.field.required ? (
                    <span aria-label="required" style={classes.shared.requiredIndicatorStyle}>
                      {' '}
                      *
                    </span>
                  ) : null}
                </Typography>

                {renderField(answer, index, !!fieldError)}

                {/* unmountOnExit re-mounts the element so role="alert" announces each time. */}
                <Collapse unmountOnExit in={!!fieldError}>
                  <Box data-validation-error role="alert" sx={classes.questions.errorTextSx()}>
                    {fieldError}
                  </Box>
                </Collapse>

                {answer.id != null && (
                  <input
                    name={`application[answers_attributes][${index}][id]`}
                    type="hidden"
                    value={answer.id}
                  />
                )}
                <input
                  name={`application[answers_attributes][${index}][field_id]`}
                  type="hidden"
                  value={answer.field.id}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Questions;
